import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { docWorkflowAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  FileText, CheckCircle, XCircle, Clock, Eye, ExternalLink,
  User, Calendar, Search, Filter, Download, AlertCircle
} from 'lucide-react';

const DocumentReview = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPendingDocuments();
  }, []);

  const fetchPendingDocuments = async () => {
    try {
      const response = await docWorkflowAPI.getPendingDocuments();
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load pending documents');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (doc) => {
    try {
      const response = await docWorkflowAPI.getDocumentDetails(doc.id);
      setSelectedDoc(response.data);
    } catch (error) {
      toast.error('Failed to load document details');
    }
  };

  const handleApprove = async (signatureId) => {
    setProcessing(true);
    try {
      await docWorkflowAPI.updateDocumentStatus(signatureId, 'approved', reviewNotes || null);
      toast.success('Document approved');
      setSelectedDoc(null);
      setReviewNotes('');
      fetchPendingDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve document');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (signatureId) => {
    if (!reviewNotes) {
      toast.error('Please provide rejection notes');
      return;
    }
    setProcessing(true);
    try {
      await docWorkflowAPI.updateDocumentStatus(signatureId, 'rejected', reviewNotes);
      toast.success('Document rejected');
      setSelectedDoc(null);
      setReviewNotes('');
      fetchPendingDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject document');
    } finally {
      setProcessing(false);
    }
  };

  const filteredDocuments = documents.filter(doc =>
    doc.document_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.signer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.signer_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (role) => {
    if (role === 'borrower') {
      return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded text-xs font-medium">Borrower</span>;
    }
    return <span className="px-2 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 rounded text-xs font-medium">LSP</span>;
  };

  if (loading) {
    return (
      <Layout title="Document Review">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  // Document Detail Modal
  if (selectedDoc) {
    return (
      <Layout title="Document Review">
        <div className="max-w-4xl mx-auto space-y-6 animate-slide-in-up">
          <Button
            variant="outline"
            onClick={() => setSelectedDoc(null)}
            className="mb-4"
          >
            ← Back to List
          </Button>

          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 gradient-primary rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                      {selectedDoc.document_name}
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Pending Admin Review
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => window.open(selectedDoc.document_url, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View PDF
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Signer Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <h4 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2">Signer Information</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-900 dark:text-slate-100">{selectedDoc.signer_name}</span>
                      {getRoleBadge(selectedDoc.signer_role)}
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{selectedDoc.signer_email}</p>
                  </div>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <h4 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2">Signature Details</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-900 dark:text-slate-100">
                        {new Date(selectedDoc.signed_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Type: {selectedDoc.signature_type}
                    </p>
                  </div>
                </div>
              </div>

              {/* Signature Preview */}
              {selectedDoc.signature_data && (
                <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <h4 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">Signature</h4>
                  <div className="flex justify-center p-4 bg-slate-50 dark:bg-slate-900 rounded">
                    <img 
                      src={selectedDoc.signature_data} 
                      alt="Signature" 
                      className="max-h-24"
                    />
                  </div>
                </div>
              )}

              {/* Form Data if present */}
              {selectedDoc.form_data && Object.keys(selectedDoc.form_data).length > 0 && (
                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <h4 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">Form Data</h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    {Object.entries(selectedDoc.form_data).map(([key, value]) => (
                      <div key={key} className="text-sm">
                        <span className="text-slate-500 dark:text-slate-400">{key.replace(/_/g, ' ')}:</span>
                        <span className="ml-2 text-slate-900 dark:text-slate-100">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deal Info if applicable */}
              {selectedDoc.deal_info && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <h4 className="text-sm font-medium text-blue-700 dark:text-blue-400 mb-2">Related Deal</h4>
                  <p className="text-slate-900 dark:text-slate-100 font-medium">
                    {selectedDoc.deal_info.deal_nickname}
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {selectedDoc.deal_info.property_city}, {selectedDoc.deal_info.property_state}
                  </p>
                </div>
              )}

              {/* Review Notes */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Review Notes (required for rejection)
                </label>
                <Input
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add any notes about this document..."
                  className="w-full"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                <Button
                  onClick={() => handleReject(selectedDoc.id)}
                  disabled={processing}
                  variant="outline"
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button
                  onClick={() => handleApprove(selectedDoc.id)}
                  disabled={processing}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Document Review">
      <div className="space-y-6 animate-slide-in-up">
        {/* Header */}
        <Card className="glass-effect border-slate-200 dark:border-slate-700">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 gradient-admin rounded-xl flex items-center justify-center shadow-lg">
                  <FileText className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Document Review</h1>
                  <p className="text-slate-600 dark:text-slate-400">Review and approve signed documents</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">{documents.length}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">Pending Review</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by document name, signer name, or email..."
            className="pl-10"
          />
        </div>

        {/* Documents List */}
        {filteredDocuments.length === 0 ? (
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-12 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                All Caught Up!
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                No documents pending review at this time.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredDocuments.map((doc) => (
              <Card 
                key={doc.id} 
                className="glass-effect border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 transition-colors cursor-pointer"
                onClick={() => handleViewDetails(doc)}
              >
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                          {doc.document_name}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            {doc.signer_name}
                          </span>
                          {getRoleBadge(doc.signer_role)}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-full text-xs font-medium">
                        <Clock className="w-3 h-3" />
                        Pending Review
                      </span>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {new Date(doc.signed_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DocumentReview;
